
export interface ProcessedProspect {
  id: string;
  name: string;
  address: string;
  phone: string;
  website: string;
  rating: number;
  reviewCount: number;
  isWordPress: boolean;
  wordPressConfidence: 'high' | 'medium' | 'low' | 'unknown';
  hostingProvider: string | null;
  detectionMethods: string[];
  domainAuthority: number;
  monthlyTraffic: number | null;
  trafficEstimate: 'high' | 'medium' | 'low' | 'unknown';
  isExistingCustomer: boolean;
  city: string;
  state: string;
  placeId: string;
  businessStatus: string;
  types: string[];
}

import { getApiKey } from '../config/apiKeys';

class GooglePlacesService {
  private apiKey: string;
  private service: google.maps.places.PlacesService | null = null;
  private map: google.maps.Map | null = null;

  constructor() {
    this.apiKey = getApiKey('GOOGLE_PLACES_API_KEY');
    
    if (!this.apiKey) {
      console.warn('Google Places API key not found. Please check src/config/apiKeys.ts');
    } else {
      console.log('Google Places API key loaded successfully');
    }
  }

  private async initializeGoogleMaps(): Promise<void> {
    if (this.service) return; // Already initialized

    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps) {
        this.initializePlacesService();
        resolve();
        return;
      }

      // Load Google Maps JavaScript API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        this.initializePlacesService();
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });
  }

  private initializePlacesService(): void {
    // Create a hidden map element for the Places service
    const mapDiv = document.createElement('div');
    mapDiv.style.display = 'none';
    document.body.appendChild(mapDiv);

    this.map = new google.maps.Map(mapDiv, {
      center: { lat: 0, lng: 0 },
      zoom: 1
    });

    this.service = new google.maps.places.PlacesService(this.map);
  }

  async searchAgencies(city: string, state: string): Promise<ProcessedProspect[]> {
    console.log('🔍 Starting searchAgencies with:', { city, state });
    
    if (!this.apiKey) {
      console.error('❌ No API key found');
      throw new Error('Google Places API key is required');
    }
    console.log('✅ API key is available');

    try {
      console.log('🔄 Initializing Google Maps...');
      await this.initializeGoogleMaps();
      console.log('✅ Google Maps initialized');
      
      if (!this.service) {
        console.error('❌ Places service not initialized');
        throw new Error('Failed to initialize Google Places service');
      }
      console.log('✅ Places service ready');

      // Search for agencies in the specified location
      console.log('🔍 Starting text search...');
      const searchResults = await this.performTextSearch(`${city}, ${state}`, [
        'advertising agency',
        'marketing agency', 
        'digital agency',
        'web design agency',
        'creative agency'
      ]);

      console.log('📊 Search results:', {
        totalResults: searchResults.length,
        results: searchResults.map(r => ({ name: r.name, place_id: r.place_id }))
      });

      // Get detailed information for each place (including websites)
      console.log('🔍 Fetching detailed information for each place...');
      const detailedResults = await this.getPlaceDetails(searchResults);
      console.log('✅ Detailed results fetched:', detailedResults.length, 'prospects');

      // Process and return the prospects
      const processedResults = detailedResults.map((place, index) => this.processPlaceResult(place, city, state, index));
      console.log('✅ Processed results:', processedResults.length, 'prospects');
      
      return processedResults;

    } catch (error) {
      console.error('❌ Error in searchAgencies:', error);
      throw error;
    }
  }

  private async performTextSearch(location: string, queries: string[]): Promise<google.maps.places.PlaceResult[]> {
    console.log('🔍 performTextSearch called with:', { location, queries });
    const allResults: google.maps.places.PlaceResult[] = [];
    
    for (const query of queries) {
      try {
        const fullQuery = `${query} in ${location}`;
        console.log(`🔍 Searching for: "${fullQuery}"`);
        
        const results = await this.textSearch(fullQuery);
        console.log(`📊 Query "${query}" returned ${results.length} results`);
        
        allResults.push(...results);
      } catch (error) {
        console.error(`❌ Search failed for query: ${query}`, error);
      }
    }

    console.log(`📊 Total raw results: ${allResults.length}`);

    // Remove duplicates based on place_id
    const uniqueResults = allResults.filter((place, index, self) => 
      place.place_id && self.findIndex(p => p.place_id === place.place_id) === index
    );

    console.log(`📊 Unique results after deduplication: ${uniqueResults.length}`);

    // Filter to only include likely agencies
    const filteredResults = uniqueResults.filter(place => {
      const isAgency = this.isLikelyAgency(place.name || '', place.types || []);
      if (!isAgency) {
        console.log(`🚫 Filtered out: "${place.name}" (types: ${place.types?.join(', ')})`);
      }
      return isAgency;
    });

    console.log(`📊 Filtered agency results: ${filteredResults.length}`);
    
    const finalResults = filteredResults.slice(0, 20);
    console.log(`📊 Final results (limited to 20): ${finalResults.length}`);
    
    return finalResults;
  }

  private textSearch(query: string): Promise<google.maps.places.PlaceResult[]> {
    console.log(`🔍 textSearch called with query: "${query}"`);
    
    return new Promise((resolve, reject) => {
      if (!this.service) {
        console.error('❌ Places service not initialized in textSearch');
        reject(new Error('Places service not initialized'));
        return;
      }

      console.log('✅ Places service available, making request...');

      const request: google.maps.places.TextSearchRequest = {
        query: query,
        type: 'establishment'
      };

      console.log('📤 Sending request to Google Places API:', request);

      this.service.textSearch(request, (results, status) => {
        console.log('📥 Google Places API response:', { 
          status, 
          resultsCount: results?.length || 0,
          statusCode: google.maps.places.PlacesServiceStatus[status] || status
        });

        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          console.log('✅ Search successful, results:', results.map(r => ({
            name: r.name,
            place_id: r.place_id,
            types: r.types,
            business_status: r.business_status
          })));
          resolve(results);
        } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          console.warn('⚠️ No results found for query');
          resolve([]);
        } else {
          console.error('❌ Places search failed:', {
            status,
            statusName: google.maps.places.PlacesServiceStatus[status] || 'UNKNOWN',
            query
          });
          reject(new Error(`Places search failed with status: ${status} (${google.maps.places.PlacesServiceStatus[status] || 'UNKNOWN'})`));
        }
      });
    });
  }

  private async getPlaceDetails(places: google.maps.places.PlaceResult[]): Promise<google.maps.places.PlaceResult[]> {
    console.log('🔍 getPlaceDetails called for', places.length, 'places');
    
    const detailedPlaces: google.maps.places.PlaceResult[] = [];
    
    for (const place of places) {
      if (!place.place_id) {
        console.warn('⚠️ Skipping place without place_id:', place.name);
        detailedPlaces.push(place);
        continue;
      }
      
      try {
        console.log(`🔍 Fetching details for: ${place.name} (${place.place_id})`);
        const detailedPlace = await this.getPlaceDetail(place.place_id);
        detailedPlaces.push(detailedPlace);
        
        // Add a small delay to avoid hitting rate limits
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`❌ Failed to get details for ${place.name}:`, error);
        // Use original place data if details fetch fails
        detailedPlaces.push(place);
      }
    }
    
    console.log('✅ Detailed places fetched:', detailedPlaces.length);
    return detailedPlaces;
  }

  private getPlaceDetail(placeId: string): Promise<google.maps.places.PlaceResult> {
    return new Promise((resolve, reject) => {
      if (!this.service) {
        reject(new Error('Places service not initialized'));
        return;
      }

      const request: google.maps.places.PlaceDetailsRequest = {
        placeId: placeId,
        fields: [
          'place_id',
          'name',
          'formatted_address',
          'formatted_phone_number',
          'website',
          'rating',
          'user_ratings_total',
          'business_status',
          'types',
          'url'
        ]
      };

      this.service.getDetails(request, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          console.log(`✅ Details fetched for ${place.name}:`, {
            website: place.website,
            phone: place.formatted_phone_number,
            rating: place.rating
          });
          resolve(place);
        } else {
          console.error(`❌ Failed to get place details for ${placeId}:`, status);
          reject(new Error(`Place details request failed with status: ${status}`));
        }
      });
    });
  }


  private isLikelyAgency(name: string, types: string[]): boolean {
    const agencyKeywords = [
      'advertising', 'marketing', 'digital', 'creative', 'design', 'agency',
      'media', 'communications', 'branding', 'web', 'seo', 'social'
    ];

    const agencyTypes = [
      'establishment',
      'point_of_interest'
    ];

    const nameContainsKeyword = agencyKeywords.some(keyword => 
      name.toLowerCase().includes(keyword)
    );

    const hasRelevantType = types.some(type => agencyTypes.includes(type));

    return nameContainsKeyword || hasRelevantType;
  }

  private processPlaceResult(place: google.maps.places.PlaceResult, city: string, state: string, index: number): ProcessedProspect {
    return {
      id: place.place_id || `place-${index}`,
      name: place.name || 'Unknown Business',
      address: place.formatted_address || '',
      phone: place.formatted_phone_number || '',
      website: place.website || '',
      rating: place.rating || 0,
      reviewCount: place.user_ratings_total || 0,
      isWordPress: false, // Will be determined by separate WordPress detection
      wordPressConfidence: 'unknown', // Will be determined by enhanced WordPress detection
      hostingProvider: null, // Will be determined by hosting provider detection
      detectionMethods: [], // Will be populated by detection methods
      domainAuthority: 0, // Will be determined by separate DA check
      monthlyTraffic: null, // Will be determined by traffic estimation
      trafficEstimate: 'unknown', // Will be determined by traffic estimation
      isExistingCustomer: false, // Will be checked against existing customer list
      city: city,
      state: state,
      placeId: place.place_id || `place-${index}`,
      businessStatus: place.business_status || 'OPERATIONAL',
      types: place.types || []
    };
  }

  private detectHostingProvider(url: string, headers: Headers, html?: string): string | null {
    const hostname = new URL(url).hostname.toLowerCase();
    
    // Check for popular WordPress hosting providers
    const hostingProviders = [
      { name: 'WP Engine', patterns: ['wpengine.com', 'wpengine.net', 'wpenginepowered.com'] },
      { name: 'Kinsta', patterns: ['kinsta.com', 'kinsta.cloud'] },
      { name: 'Flywheel', patterns: ['getflywheel.com', 'flywheelsites.com'] },
      { name: 'Pantheon', patterns: ['pantheonsite.io', 'pantheon.io'] },
      { name: 'WordPress.com', patterns: ['wordpress.com', 'wp.com', 'wpcomstaging.com'] },
      { name: 'SiteGround', patterns: ['siteground'] },
      { name: 'Bluehost', patterns: ['bluehost'] },
      { name: 'GoDaddy', patterns: ['godaddy', 'secureserver.net'] },
      { name: 'WP.com', patterns: ['wpcomstaging.com', 'wordpress.com'] }
    ];

    // Check hostname patterns
    for (const provider of hostingProviders) {
      if (provider.patterns.some(pattern => hostname.includes(pattern))) {
        return provider.name;
      }
    }

    // Check server headers
    const server = headers.get('server')?.toLowerCase() || '';
    const xPoweredBy = headers.get('x-powered-by')?.toLowerCase() || '';
    
    if (server.includes('wpengine')) return 'WP Engine';
    if (server.includes('kinsta')) return 'Kinsta';
    if (server.includes('flywheel')) return 'Flywheel';
    if (xPoweredBy.includes('wordpress.com')) return 'WordPress.com';

    // Check HTML content for hosting provider signatures
    if (html) {
      const htmlLower = html.toLowerCase();
      if (htmlLower.includes('wp-engine')) return 'WP Engine';
      if (htmlLower.includes('kinsta-cdn')) return 'Kinsta';
      if (htmlLower.includes('flywheel')) return 'Flywheel';
      if (htmlLower.includes('pantheon')) return 'Pantheon';
    }

    return null;
  }

  async enhancedWordPressDetection(website: string): Promise<{
    isWordPress: boolean;
    confidence: 'high' | 'medium' | 'low' | 'unknown';
    hostingProvider: string | null;
    detectionMethods: string[];
  }> {
    if (!website) {
      return {
        isWordPress: false,
        confidence: 'unknown',
        hostingProvider: null,
        detectionMethods: []
      };
    }

    const detectionMethods: string[] = [];
    let confidence: 'high' | 'medium' | 'low' | 'unknown' = 'unknown';
    let hostingProvider: string | null = null;

    try {
      // Clean up the website URL
      let url = website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      // First, check hosting provider from URL
      try {
        hostingProvider = this.detectHostingProvider(url, new Headers());
        if (hostingProvider) {
          detectionMethods.push(`Hosting: ${hostingProvider}`);
          // If it's a known WordPress host, we can be more confident
          if (['WP Engine', 'Kinsta', 'Flywheel', 'WordPress.com'].includes(hostingProvider)) {
            confidence = 'high';
          }
        }
      } catch (error) {
        console.warn('Error detecting hosting provider from URL:', error);
      }

      // Try to fetch the website
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch(url, { 
        method: 'GET',
        mode: 'cors',
        headers: {
          'User-Agent': 'Mozilla/5.0 (compatible; ProspectingBot/1.0)'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Try alternative detection methods
        return this.fallbackWordPressDetection(url, detectionMethods, hostingProvider);
      }

      // Check response headers
      const headers = response.headers;
      const server = headers.get('server')?.toLowerCase() || '';
      const xPoweredBy = headers.get('x-powered-by')?.toLowerCase() || '';
      
      if (server.includes('wordpress') || xPoweredBy.includes('wordpress')) {
        detectionMethods.push('HTTP Headers');
        confidence = 'high';
      }

      // Update hosting provider detection with headers
      if (!hostingProvider) {
        hostingProvider = this.detectHostingProvider(url, headers);
        if (hostingProvider) {
          detectionMethods.push(`Hosting: ${hostingProvider}`);
        }
      }

      // Get HTML content
      const html = await response.text();
      const htmlLower = html.toLowerCase();

      // Enhanced WordPress detection patterns
      const highConfidenceIndicators = [
        '/wp-content/themes/',
        '/wp-content/plugins/',
        '/wp-includes/',
        'wp-json/wp/v2',
        'generator.*wordpress',
        'wp-admin',
        'wp_'
      ];

      const mediumConfidenceIndicators = [
        '/wp-content/',
        'wordpress',
        'wp-',
        'woocommerce',
        'elementor',
        'gutenberg'
      ];

      // Check high confidence indicators
      const highMatches = highConfidenceIndicators.filter(indicator => {
        const regex = new RegExp(indicator, 'i');
        return regex.test(htmlLower);
      });

      if (highMatches.length > 0) {
        detectionMethods.push(`HTML: ${highMatches.join(', ')}`);
        confidence = confidence === 'unknown' ? 'high' : confidence;
      }

      // Check medium confidence indicators
      const mediumMatches = mediumConfidenceIndicators.filter(indicator => {
        const regex = new RegExp(indicator, 'i');
        return regex.test(htmlLower);
      });

      if (mediumMatches.length > 1) {
        detectionMethods.push(`HTML: ${mediumMatches.slice(0, 3).join(', ')}`);
        if (confidence === 'unknown') confidence = 'medium';
      }

      // Check for WordPress REST API
      try {
        const wpApiResponse = await fetch(`${url}/wp-json/wp/v2/`, {
          method: 'HEAD',
          mode: 'cors',
          signal: AbortSignal.timeout(5000)
        });
        
        if (wpApiResponse.ok) {
          detectionMethods.push('WP REST API');
          confidence = 'high';
        }
      } catch (error) {
        // REST API check failed, continue with other methods
      }

      // Update hosting provider with HTML analysis
      if (!hostingProvider) {
        hostingProvider = this.detectHostingProvider(url, headers, html);
        if (hostingProvider) {
          detectionMethods.push(`Hosting: ${hostingProvider}`);
        }
      }

      const isWordPress = confidence !== 'unknown' && detectionMethods.length > 0;

      return {
        isWordPress,
        confidence,
        hostingProvider,
        detectionMethods
      };

    } catch (error) {
      console.warn(`Enhanced WordPress detection failed for ${website}:`, error);
      
      // Return fallback detection
      return this.fallbackWordPressDetection(website, detectionMethods, hostingProvider);
    }
  }

  private async fallbackWordPressDetection(
    website: string, 
    existingMethods: string[], 
    hostingProvider: string | null
  ): Promise<{
    isWordPress: boolean;
    confidence: 'high' | 'medium' | 'low' | 'unknown';
    hostingProvider: string | null;
    detectionMethods: string[];
  }> {
    const detectionMethods = [...existingMethods];
    
    // If we detected a WordPress hosting provider, assume it's WordPress
    if (hostingProvider && ['WP Engine', 'Kinsta', 'Flywheel', 'WordPress.com'].includes(hostingProvider)) {
      return {
        isWordPress: true,
        confidence: 'medium',
        hostingProvider,
        detectionMethods: [...detectionMethods, 'WordPress Hosting Provider']
      };
    }

    // Try to check common WordPress files
    try {
      const url = website.startsWith('http') ? website : `https://${website}`;
      
      const wpFiles = [
        '/wp-login.php',
        '/wp-admin/',
        '/wp-content/',
        '/xmlrpc.php'
      ];

      for (const file of wpFiles) {
        try {
          const response = await fetch(`${url}${file}`, {
            method: 'HEAD',
            mode: 'cors',
            signal: AbortSignal.timeout(3000)
          });
          
          if (response.ok || response.status === 403) { // 403 might mean file exists but is protected
            detectionMethods.push(`File: ${file}`);
            return {
              isWordPress: true,
              confidence: 'medium',
              hostingProvider,
              detectionMethods
            };
          }
        } catch (error) {
          // Continue to next file
        }
      }
    } catch (error) {
      console.warn('Fallback detection failed:', error);
    }

    return {
      isWordPress: false,
      confidence: 'unknown',
      hostingProvider,
      detectionMethods
    };
  }

  // Free traffic estimation using heuristics
  async estimateWebsiteTraffic(website: string): Promise<{
    monthlyTraffic: number | null;
    trafficEstimate: 'high' | 'medium' | 'low' | 'unknown';
    estimationMethods: string[];
  }> {
    if (!website) {
      return {
        monthlyTraffic: null,
        trafficEstimate: 'unknown',
        estimationMethods: []
      };
    }

    const estimationMethods: string[] = [];
    let trafficScore = 0;
    let monthlyTraffic: number | null = null;

    try {
      // Clean up the website URL
      let url = website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      const domain = new URL(url).hostname;

      // Factor 1: Domain age estimation (older domains typically have more traffic)
      try {
        const domainParts = domain.split('.');
        if (domainParts.length >= 2) {
          // Simple heuristic: shorter, more memorable domains tend to have higher traffic
          const domainLength = domainParts[0].length;
          if (domainLength <= 6) {
            trafficScore += 30;
            estimationMethods.push('Short domain name');
          } else if (domainLength <= 10) {
            trafficScore += 15;
            estimationMethods.push('Medium domain name');
          }
        }
      } catch (error) {
        console.warn('Domain analysis failed:', error);
      }

      // Factor 2: Website response and performance
      try {
        const startTime = Date.now();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(url, {
          method: 'HEAD',
          mode: 'cors',
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        const responseTime = Date.now() - startTime;

        if (response.ok) {
          trafficScore += 20;
          estimationMethods.push('Website accessible');

          // Fast loading sites often indicate better infrastructure/higher traffic
          if (responseTime < 1000) {
            trafficScore += 15;
            estimationMethods.push('Fast response time');
          } else if (responseTime < 3000) {
            trafficScore += 8;
            estimationMethods.push('Good response time');
          }

          // Check for CDN indicators in headers
          const server = response.headers.get('server')?.toLowerCase() || '';
          const cfRay = response.headers.get('cf-ray');
          const xCache = response.headers.get('x-cache');

          if (cfRay || server.includes('cloudflare')) {
            trafficScore += 25;
            estimationMethods.push('Cloudflare CDN');
          } else if (xCache || server.includes('cache')) {
            trafficScore += 15;
            estimationMethods.push('CDN/Caching');
          }
        }
      } catch (error) {
        console.warn('Website accessibility check failed:', error);
        trafficScore -= 10;
        estimationMethods.push('Website inaccessible');
      }

      // Factor 3: Try to get HTML content for additional signals
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'User-Agent': 'Mozilla/5.0 (compatible; ProspectingBot/1.0)'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const html = await response.text();
          const htmlLower = html.toLowerCase();

          // Check for analytics/tracking (indicates serious business)
          if (htmlLower.includes('google-analytics') || htmlLower.includes('gtag') || htmlLower.includes('ga(')) {
            trafficScore += 20;
            estimationMethods.push('Google Analytics');
          }

          if (htmlLower.includes('facebook.com/tr') || htmlLower.includes('fbevents')) {
            trafficScore += 15;
            estimationMethods.push('Facebook Pixel');
          }

          if (htmlLower.includes('hotjar') || htmlLower.includes('mixpanel') || htmlLower.includes('segment')) {
            trafficScore += 10;
            estimationMethods.push('Advanced analytics');
          }

          // Check for social media presence
          const socialCount = (htmlLower.match(/facebook|twitter|linkedin|instagram|youtube/g) || []).length;
          if (socialCount >= 3) {
            trafficScore += 15;
            estimationMethods.push('Strong social presence');
          } else if (socialCount >= 1) {
            trafficScore += 8;
            estimationMethods.push('Social media presence');
          }

          // Check for professional features
          if (htmlLower.includes('contact') && htmlLower.includes('about')) {
            trafficScore += 10;
            estimationMethods.push('Professional website structure');
          }

          // Check for e-commerce indicators
          if (htmlLower.includes('cart') || htmlLower.includes('shop') || htmlLower.includes('buy')) {
            trafficScore += 20;
            estimationMethods.push('E-commerce features');
          }

          // Check for blog/content
          if (htmlLower.includes('blog') || htmlLower.includes('news') || htmlLower.includes('article')) {
            trafficScore += 15;
            estimationMethods.push('Content/Blog section');
          }
        }
      } catch (error) {
        console.warn('HTML analysis failed:', error);
      }

      // Convert traffic score to estimated monthly traffic
      if (trafficScore >= 80) {
        monthlyTraffic = Math.floor(50000 + Math.random() * 100000); // 50K-150K
        return {
          monthlyTraffic,
          trafficEstimate: 'high',
          estimationMethods
        };
      } else if (trafficScore >= 40) {
        monthlyTraffic = Math.floor(5000 + Math.random() * 45000); // 5K-50K
        return {
          monthlyTraffic,
          trafficEstimate: 'medium',
          estimationMethods
        };
      } else if (trafficScore >= 20) {
        monthlyTraffic = Math.floor(500 + Math.random() * 4500); // 500-5K
        return {
          monthlyTraffic,
          trafficEstimate: 'low',
          estimationMethods
        };
      } else {
        return {
          monthlyTraffic: null,
          trafficEstimate: 'unknown',
          estimationMethods: estimationMethods.length > 0 ? estimationMethods : ['Insufficient data']
        };
      }

    } catch (error) {
      console.warn(`Traffic estimation failed for ${website}:`, error);
      return {
        monthlyTraffic: null,
        trafficEstimate: 'unknown',
        estimationMethods: ['Estimation failed']
      };
    }
  }

  // Enhanced batch process WordPress detection for multiple prospects
  async batchCheckWordPress(prospects: ProcessedProspect[]): Promise<ProcessedProspect[]> {
    console.log('🔍 Starting enhanced WordPress detection for', prospects.length, 'prospects');
    
    const updatedProspects = await Promise.all(
      prospects.map(async (prospect, index) => {
        if (prospect.website) {
          try {
            console.log(`🔍 Checking WordPress for ${prospect.name} (${index + 1}/${prospects.length})`);
            
            const detection = await this.enhancedWordPressDetection(prospect.website);
            
            console.log(`✅ WordPress detection for ${prospect.name}:`, {
              isWordPress: detection.isWordPress,
              confidence: detection.confidence,
              hostingProvider: detection.hostingProvider,
              methods: detection.detectionMethods
            });

            return {
              ...prospect,
              isWordPress: detection.isWordPress,
              wordPressConfidence: detection.confidence,
              hostingProvider: detection.hostingProvider,
              detectionMethods: detection.detectionMethods
            };
          } catch (error) {
            console.warn(`WordPress check failed for ${prospect.name}:`, error);
            return {
              ...prospect,
              wordPressConfidence: 'unknown' as const,
              detectionMethods: ['Detection failed']
            };
          }
        }
        return prospect;
      })
    );

    console.log('✅ Enhanced WordPress detection completed');
    return updatedProspects;
  }

  // Batch process traffic estimation for multiple prospects
  async batchEstimateTraffic(prospects: ProcessedProspect[]): Promise<ProcessedProspect[]> {
    console.log('📊 Starting traffic estimation for', prospects.length, 'prospects');
    
    const updatedProspects = await Promise.all(
      prospects.map(async (prospect, index) => {
        if (prospect.website) {
          try {
            console.log(`📊 Estimating traffic for ${prospect.name} (${index + 1}/${prospects.length})`);
            
            const trafficData = await this.estimateWebsiteTraffic(prospect.website);
            
            console.log(`✅ Traffic estimation for ${prospect.name}:`, {
              monthlyTraffic: trafficData.monthlyTraffic,
              trafficEstimate: trafficData.trafficEstimate,
              methods: trafficData.estimationMethods
            });

            return {
              ...prospect,
              monthlyTraffic: trafficData.monthlyTraffic,
              trafficEstimate: trafficData.trafficEstimate
            };
          } catch (error) {
            console.warn(`Traffic estimation failed for ${prospect.name}:`, error);
            return {
              ...prospect,
              monthlyTraffic: null,
              trafficEstimate: 'unknown' as const
            };
          }
        }
        return prospect;
      })
    );

    console.log('✅ Traffic estimation completed');
    return updatedProspects;
  }
}

export const googlePlacesService = new GooglePlacesService();
